type Color = {
    r: number,
    g: number,
    b: number,
}

type ColorWrapper = {
    color: Color,
}

type Fresnel = {
    bias: number,
    power: number,
    scale: number,
    backfaceFactor: number,
}

interface IConfig {
    readonly maxHistoryUndos: number,
    readonly selectionColor: Color,
    readonly deselectionColor: Color,
    readonly xrayBackgroundColor: Color,
    readonly outlineColor: Color,
    readonly cadOutlineColor: Color,
    readonly diffuseReflectivity: number[],
    readonly textureReflectivity: number[],
    readonly widgetsLights: ColorWrapper[],
    readonly lights: ColorWrapper[],
    readonly cutOffPlaneFColor: Color,
    readonly cutOffPlaneBColor: Color,
    readonly maxErrorFusionColor: Color,
    readonly geometryMemoryLimit: number, // bytes
    readonly greenErrorColor: number[],
    readonly yellowErrorColor: number[],
    readonly orangeErrorColor: number[],
    readonly redErrorColor: number[],
    readonly darkRedErrorColor: number[],
    readonly scansTextureLevel: number,
    readonly maxPixelRatio: number,
    readonly modelWithWireframeOpacity: number,
    readonly nodeMaxImageSize: number,
    readonly enableAntialiasing: boolean,
    readonly postBlending: boolean,
    readonly surfelsFixedPointSize: number,
    readonly frensel: Fresnel,
    readonly drawQueries: boolean,

    backgroundColor: Color,
    backgroundTexture: string,
    cacheUnloadedData: boolean,
    viewOnlyMode: boolean,
    useWorkers: boolean,
    dropTextures: boolean,
    enableRendering: boolean,
    clientAlign: boolean,
    enableManipulatorsWarnings: boolean,
    adaptiveSurfelsFiltering: boolean,
    maxWorkersPerTask: number,
    recalculateInterfaceDepth: boolean,
    lodPreload: boolean,
    perObjectEditing: boolean,
}

const config: IConfig = {
    maxHistoryUndos: 20,
    selectionColor: {r: 0.851, g: 0.337, b: 0.337},
    deselectionColor: {r: 0.54, g: 0.86, b: 0.6},
    xrayBackgroundColor: {r: 0.1, g: 0.1, b: 0.1},
    outlineColor: {r: 0.18, g: 0.5, b: 0.93},
    cadOutlineColor: {r: 1.0, g: 1.0, b: 1.0},
    diffuseReflectivity: [0.11, 0.55, 0.33],
    textureReflectivity: [0.11, 1.0, 0.33],
    widgetsLights: [
        {color: {r: 1, g: 1, b: 1}}
    ],
    lights: [
        {color: {r: 1, g: 1, b: 1}}
    ],
    cutOffPlaneFColor: {r: 0.0, g: 0.8, b: 0.0},
    cutOffPlaneBColor: {r: 0.8, g: 0.0, b: 0.0},
    maxErrorFusionColor: {r: 0.6, g: 0.6, b: 0.6},
    geometryMemoryLimit: 500000000, // bytes
    greenErrorColor: [0.0, 1.0, 0.0],
    yellowErrorColor: [1.0, 1.0, 0.0],
    orangeErrorColor: [1.0, 0.65, 0.0],
    redErrorColor: [1.0, 0.0, 0.0],
    darkRedErrorColor: [0.4, 0.0, 0.0],
    scansTextureLevel: 0,
    maxPixelRatio: 2,
    modelWithWireframeOpacity: 0.1,
    nodeMaxImageSize: 4096,
    enableAntialiasing: true,
    postBlending: true,
    surfelsFixedPointSize: 3.0,
    frensel: {
        bias: 0.3,
        power: 2.0,
        scale: 0.8,
        backfaceFactor: 0.8,
    },
    drawQueries: false,

    backgroundColor: {r: 0.898, g: 0.898, b: 0.898},
    backgroundTexture: "",
    cacheUnloadedData: false,
    viewOnlyMode: false,
    useWorkers: false,
    dropTextures: false,
    enableRendering: true,
    clientAlign: false,
    enableManipulatorsWarnings: false,
    adaptiveSurfelsFiltering: false,
    maxWorkersPerTask: 4,
    recalculateInterfaceDepth: false,
    lodPreload: false,
    perObjectEditing: false,
};

export default config;
